import React from 'react';
import {Autocomplete, Box, SxProps, TextField, Typography, useTheme} from '@mui/material';
import {Theme} from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import CheckIcon from '@mui/icons-material/Check';
import {Pallete} from '../../theme/bluebird';

export interface Option {
  key: string;
  label: string;
  selectedColor?: string;
  picture?: string;
}

interface SelectDropdownProps {
  options: Option[];
  selectedOption?: Option;
  onChange: (selectedOption: Option) => void;
  isOptionEqualToValue?: (option: Option, value: Option) => boolean;
  sx?: SxProps<Theme>;
}

export const SelectDropdown = (props: SelectDropdownProps) => {
  const theme = useTheme();

  const hint = React.useRef('');
  const [inputValue, setInputValue] = React.useState(props.selectedOption?.label || '');

  const [selectedOption, setSelectedOption] = React.useState<Option | undefined>(
    props.selectedOption
  );

  const handleChange = (event: React.ChangeEvent<{}>, selectedOption: Option) => {
    hint.current = '';
    setInputValue(selectedOption.label);
    setSelectedOption(selectedOption);
    props.onChange(selectedOption);
  };

  return (
    <Autocomplete
      disableClearable
      onKeyDown={(event) => {
        if (event.key === 'Tab') {
          if (hint.current) {
            setInputValue(hint.current);
            event.preventDefault();
          }
        }
      }}
      onBlur={() => {
        hint.current = '';
      }}
      disablePortal
      inputValue={inputValue}
      filterOptions={(displayOptions, state) => {
        return displayOptions.filter(
          (option) =>
            option.label.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim()) ||
            option.key.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim())
        );
      }}
      isOptionEqualToValue={props.isOptionEqualToValue}
      options={props.options}
      value={props.selectedOption}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      renderOption={(props, option, state) => {
        return (
          <li
            {...props}
            key={option.key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                whiteSpace: 'initial',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {option.label}
            </Typography>
            {state.selected ? (
              <SvgIcon sx={{fontSize: 'small'}}>
                <CheckIcon />
              </SvgIcon>
            ) : null}
          </li>
        );
      }}
      renderInput={(params) => {
        return (
          <Box sx={{position: 'relative'}}>
            <Typography
              component="p"
              sx={{
                display: 'flex',
                position: 'absolute',
                opacity: 0.5,
                height: '100%',
                py: '8px',
                px: 0,
                color: Pallete.blue.base,
                fontWeight: '600',
                width: '129px',
                overflow: 'hidden',
              }}
            >
              <span style={{visibility: 'hidden', whiteSpace: 'pre'}}>
                {hint.current.slice(0, inputValue.length)}
              </span>
              <span style={{whiteSpace: 'pre'}}>
                {hint.current.slice(inputValue.length, hint.current.length)}
              </span>
            </Typography>
            <TextField
              {...params}
              variant="outlined"
              InputLabelProps={{children: null}}
              onChange={(e) => {
                const newValue = e.target.value;
                setInputValue(newValue);
                const matchingOption = props.options.find((option) =>
                  option.label.startsWith(newValue)
                );

                if (newValue && matchingOption) {
                  hint.current = matchingOption.label;
                } else {
                  hint.current = '';
                }
              }}
              sx={{
                '& input': {color: selectedOption?.selectedColor},
                '& fieldset': {border: 'none'},
              }}
            />
          </Box>
        );
      }}
      sx={{
        backgroundColor: theme.palette.grey[200],
        borderRadius: '5px',
        '& .MuiFormControl-root': {
          height: '100%',
        },
        '& .MuiInputBase-root': {
          height: '100%',
          py: 0,
          px: 0,
          color: Pallete.blue.base,
          fontWeight: '600',
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          px: 0,
          py: '8px',
        },
        ...props.sx,
      }}
    />
  );
};
